<template>
  <div class="dt">
    <DatePicker
      :model-value="day"
      future
      :min="minDay"
      :clearable="clearable"
      :disabled="disabled"
      :label="label"
      :placeholder="placeholder"
      @update:model-value="pickDay"
    />
    <input
      class="dt-time"
      type="time"
      :value="time"
      :min="minTime"
      :disabled="disabled || !day"
      :aria-label="$trf('{0}: time', [label || $tr('Date')])"
      @input="pickTime"
    />
  </div>
</template>

<script>
import DatePicker from "./DatePicker.vue";

/**
 * <DateTimeInput v-model="coupon.c_date_start" :min="earliest" />: a day on the panel's
 * calendar (DatePicker, Google Ads' shape, owner 2026-10-23) and a time beside it. It replaced
 * vue-ctk-date-time-picker (Vue 2 only) in the coupon and push notification forms, then the
 * browser's own date-time field, and keeps what those forms rely on:
 * - the model is text "YYYY-MM-DD HH:mm", the format the API stores, so nothing changes on the
 *   server; values that also carry seconds or a "T" still show;
 * - it reports only what the person picks, and null when the day is cleared or the time
 *   emptied, never anything on load;
 * - `min` (same format) keeps earlier days off the calendar and earlier times off that day.
 * A day picked while no time is set takes `default-time` (midnight unless the form says), and
 * on `min`'s day no earlier than `min`'s time.
 */
const MODEL_FORMAT = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/;

function parts(value) {
  const m = MODEL_FORMAT.exec(value == null ? "" : String(value));
  return m ? { day: m[1], time: m[2] } : { day: "", time: "" };
}

export default {
  name: "DateTimeInput",
  components: { DatePicker },
  props: {
    modelValue: { type: String, default: null },
    min: { type: String, default: null },
    defaultTime: { type: String, default: "00:00" },
    clearable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    const { day, time } = parts(this.modelValue);
    // What was last reported: the form's answer to it is not news, so a day picked while the
    // time is still empty stays on screen.
    return { day, time, sent: this.modelValue };
  },
  computed: {
    minDay() {
      return parts(this.min).day;
    },
    minTime() {
      const m = parts(this.min);
      return m.day && m.day === this.day ? m.time : null;
    },
  },
  watch: {
    modelValue(value) {
      if (value === this.sent) return;
      const { day, time } = parts(value);
      this.day = day;
      this.time = time;
      this.sent = value;
    },
  },
  methods: {
    pickDay(day) {
      this.day = day || "";
      // A cleared day clears the moment; a new day without a time takes the form's default time,
      // and on `min`'s own day never a time before it (today at 00:00 would already be past).
      if (!this.day) this.time = "";
      else if (!this.time) this.time = this.defaultTime;
      if (this.day && this.minTime && this.time < this.minTime) this.time = this.minTime;
      this.report();
    },
    pickTime(event) {
      this.time = event.target.value || "";
      this.report();
    },
    report() {
      const value = this.day && this.time ? this.day + " " + this.time : null;
      if (value === this.sent) return;
      this.sent = value;
      this.$emit("update:modelValue", value);
    },
  },
};
</script>

<style scoped>
.dt {
  --dp-trigger-h: var(--input-height, 42px);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
/* The panel's text field look (styles.css, dark.css), which only names the text input
   types, so it is repeated here rather than widened for every time field. */
.dt-time {
  box-sizing: border-box;
  height: var(--input-height, 42px);
  padding: 0 0.5rem;
  font-family: var(--font-main, inherit);
  font-size: 0.875rem;
  color: var(--text-primary);
  background-color: var(--bg-input);
  border: 1.5px solid var(--form-input-border);
  border-radius: 8px;
  outline: none;
}
.dt-time:focus {
  border-color: var(--border-focus);
  background: var(--bg-primary);
  box-shadow: 0 0 0 3px var(--brand-orange-glow, rgba(249, 115, 22, 0.15));
}
.dt-time:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
